import React, {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const getOptions = children =>
  Children.toArray(children).filter(isValidElement);

const Select = forwardRef((props, ref) => {
  const {
    children,
    className = "",
    containerClassName = "",
    disabled,
    error,
    fullWidth,
    helperText,
    id,
    label,
    labelId,
    margin,
    name,
    onChange,
    required,
    value,
    variant,
    ...selectProps
  } = props;

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const options = getOptions(children);
  const selectedOption = options.find(
    option => String(option.props.value) === String(value)
  );

  const setTriggerRef = node => {
    triggerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const menuHeight = menuRef.current?.offsetHeight || 0;
    const openUpwards = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      top: openUpwards ? undefined : rect.bottom + 4,
      bottom: openUpwards ? viewportHeight - rect.top + 4 : undefined,
      maxHeight: Math.max(160, (openUpwards ? spaceAbove : spaceBelow) - 12),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => updateMenuPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    const handleClickOutside = event => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = event => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen(prev => !prev);
  };

  const handleTriggerKeyDown = event => {
    if (disabled) return;
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleSelect = option => {
    if (option.props.disabled) return;

    if (onChange) {
      onChange({ target: { name, value: option.props.value } });
    }
    setOpen(false);
    triggerRef.current?.focus();
  };

  const hasLabel = Boolean(label);

  return (
    <div className={`relative w-full ${hasLabel ? "pt-2" : ""} ${containerClassName}`}>
      {label && (
        <label
          id={labelId}
          htmlFor={id || name}
          className="absolute left-2 top-0 z-[1] bg-farmazon-surface px-1 text-[11px] font-semibold leading-4 text-farmazon-text"
        >
          {label}
          {required ? " *" : ""}
        </label>
      )}
      <button
        {...selectProps}
        type="button"
        id={id || name}
        ref={setTriggerRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        className={`flex h-10 w-full cursor-pointer items-center rounded-lg border border-solid bg-[var(--farmazon-input)] px-3 pr-9 text-left text-sm text-farmazon-text outline-none transition-colors hover:border-slate-400 focus:border-green-600 focus:ring-2 focus:ring-green-600/10 disabled:cursor-not-allowed disabled:opacity-60 ${
          error ? "border-red-500" : "border-[var(--farmazon-input-border)]"
        } ${className}`}
      >
        <span className={`truncate ${!selectedOption ? "text-farmazon-text-faint" : ""}`}>
          {selectedOption ? selectedOption.props.children : ""}
        </span>
      </button>
      <span className={`pointer-events-none absolute right-3 ${hasLabel ? "top-7" : "top-5"} -translate-y-1/2 text-[10px] text-farmazon-text-muted`}>
        ▼
      </span>
      {helperText && (
        <p className={`mb-0 mt-1 text-xs ${error ? "text-red-600" : "text-farmazon-text-muted"}`}>
          {helperText}
        </p>
      )}
      {open &&
        !disabled &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={menuStyle || { visibility: "hidden" }}
            className="z-[9000] overflow-y-auto rounded-lg border border-farmazon-border bg-farmazon-surface py-1 shadow-lg"
          >
            {options.map((option, index) => {
              const isSelected = String(option.props.value) === String(value);
              return (
                <div
                  key={option.key ?? `${option.props.value}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.props.disabled || undefined}
                  onClick={() => handleSelect(option)}
                  className={`px-3 py-2 text-sm ${
                    option.props.disabled
                      ? "cursor-not-allowed text-farmazon-text-faint"
                      : "cursor-pointer hover:bg-farmazon-bg"
                  } ${isSelected ? "bg-farmazon-bg font-medium text-[var(--primaryColor)]" : "text-farmazon-text"} ${
                    option.props.className || ""
                  }`}
                >
                  {option.props.children}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
});

Select.displayName = "Select";

export default Select;
